import React from "react";

import JobsPagination from "./JobsPagination";

const IN_PROGRESS_STATUSES = new Set(["queued", "running"]);
const SCHEDULED_STATUS = "scheduled";

// Split the visible jobs into the three buckets the UI groups them by. Active
// (queued/running) jobs are always present in the list; scheduled and history
// jobs come from the current page. Statuses outside these buckets fall into
// history so nothing is ever silently dropped.
function partitionJobs(jobs) {
  const running = [];
  const scheduled = [];
  const history = [];
  jobs.forEach((job) => {
    const status = job?.status;
    if (IN_PROGRESS_STATUSES.has(status)) running.push(job);
    else if (status === SCHEDULED_STATUS) scheduled.push(job);
    else history.push(job);
  });
  return { running, scheduled, history };
}

/**
 * Shared chrome for the async-tool job listings. Renders the panel header with
 * at-a-glance counts (scheduled / completed), groups jobs into In progress →
 * Scheduled → History with visual separation, and the pagination footer.
 *
 * Each tool keeps its own per-job card markup by passing a `renderJob(job)`
 * render-prop — no job/poll/download/cancel logic lives here, only layout.
 */
export default function ToolJobsSection({
  title,
  jobs = [],
  emptyText = "No jobs yet.",
  renderJob,
  page,
  totalPages,
  totalFinished,
  onPageChange,
}) {
  const { running, scheduled, history } = partitionJobs(jobs);
  const hasJobs = jobs.length > 0;

  const groups = [
    { key: "running", label: "In progress", items: running },
    { key: "scheduled", label: "Scheduled", items: scheduled },
    { key: "history", label: "History", items: history },
  ].filter((group) => group.items.length > 0);

  return (
    <section className="tool-section tool-jobs-panel">
      <div className="tool-jobs-panel-header">
        <h3 className="tool-section-title">{title}</h3>
        {hasJobs ? (
          <div className="tool-jobs-summary">
            {running.length > 0 ? (
              <span className="tool-jobs-chip tool-jobs-chip-running">
                {running.length} running
              </span>
            ) : null}
            <span className="tool-jobs-chip tool-jobs-chip-scheduled">
              {scheduled.length} scheduled
            </span>
            <span className="tool-jobs-chip tool-jobs-chip-history">
              {history.length} completed
            </span>
          </div>
        ) : null}
      </div>

      {hasJobs ? (
        <div className="tool-jobs-groups">
          {groups.map((group) => (
            <div
              key={group.key}
              className={`tool-jobs-group tool-jobs-group-${group.key}`}
            >
              <div className="tool-jobs-group-head">
                <span className="tool-jobs-group-label">{group.label}</span>
                <span className="tool-jobs-group-count">{group.items.length}</span>
              </div>
              <div className="tool-job-list">
                {group.items.map((job) => renderJob(job))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="tool-helper-text">{emptyText}</p>
      )}

      <JobsPagination
        page={page}
        totalPages={totalPages}
        totalFinished={totalFinished}
        onPageChange={onPageChange}
      />
    </section>
  );
}
